/* eslint-disable prefer-destructuring */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import { Box, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '../../../../redux/hooks';
import { getAcsUserByCommunicationId } from '../../../../routes/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useXpPoints } from '../../components/menteeXpPoints';
import MySkeleton from '../../../../components/Skeleton';
import { showMyLoader } from '../../components/common';
import { getUserDetails } from '../../../../utils/orgName';
import { getProgramContext } from '../../../../hooks/useProgramContext';
import { ChatEditorLoader, ChatMainHeaderLoader, ChatMsgContentLoader } from '../../../../Loader/chat/ChatLoader';
import { useIsMobileApp } from '../../../../mobile/mobileApp';

// const handleScrollChannels = () => {
//   const collection = document.getElementsByClassName('scroll-channel')[0];
//   collection.scrollTo(0, 0);
// };

interface INoChats {
  activeChat?: string;
}

interface ChatBoxProps {
  icon?: string;
  heading: string;
  subheading?: string;
  buttonText?: string;
  onClick?: () => void;
  btnLoading?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const NoChats: React.FC<INoChats> = ({ activeChat }) => {
  // const role = localStorage.getItem('role');
  const selectedChatUser = useAppSelector((state) => state.selectedChatUser.data);
  const getXpPoints = useXpPoints();
  // const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const commId = searchParams.get('commId') || '';
  // const acsOrgUsers = useAppSelector(state => state.acsOrgUsers.data)
  const acsCommunityUsers = useAppSelector((state) => state.acsCommunityUsers.data);
  const { user } = useAuth0();
  const [channelCreateLoading, setChannelCreateLoading] = useState(false);
  const acsToken = useAppSelector((state) => state.acsToken.data.token);
  const participant = getAcsUserByCommunicationId(acsCommunityUsers, commId);
  // const { mentee } = useProfileDetails();
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  const urlSearchParams = new URLSearchParams(document.location.hash);
  // const chatType = urlSearchParams.get('type') || 'Chats';
  const loading = showMyLoader();
  const navigate = useNavigate();
  const isMobileApp = useIsMobileApp();
  const hasTriggeredCreate = useRef(false);

  const handleClickOnStartChat = async () => {
    const { orgId } = getUserDetails();
    const modifiedValues = {
      participants: [
        {
          ...participant,
          displayName: _.get(participant, 'id.communicationUserId', ''),
          id: {
            communicationUserId: _.get(participant, 'id.communicationUserId', '')
          }
        }
      ],
      topic: '#personal',
      azureToken: acsToken
    };
    setChannelCreateLoading(true);
    try {
      const { status, data } = await API.createACSThread(acsToken, modifiedValues, orgId);
      if (status === 200) {
        setTimeout(async () => {
          await queryClient.fetchQuery({ queryKey: ['getAcsChatThreads'] }).then((res) => {
            setChannelCreateLoading(false);
            // Phone: the new thread takes the ?commId entry's place, so back returns to where the
            // chat was started instead of to ?commId (which would resolve straight back into it).
            navigate(
              `/app/chat/?threadid=${data.threadId}&name=${_.get(participant, 'id.communicationUserId', '')}&type=Chats`,
              { replace: isMobileApp }
            );
          });
        }, 500);

        if (selectedGroup.role === 'mentee') {
          getXpPoints('mentee');
        }
      }
    } catch (err) {
      setChannelCreateLoading(false);
      toast.error(_.get(err, 'response.data', ''));
    }
  };

  // When we have commId (user clicked someone or came from redirect), open the conversation with them:
  // the one the user already has when the server lists it, a new one otherwise. The thread list may not
  // be in yet (a refused chat token is being renewed), and creating then started a duplicate
  // conversation (docs/BUG-chat-duplicate-dm-threads.md). A list the server refused (the call resolves
  // to nothing) waits for the next token instead.
  useEffect(() => {
    if (!commId || hasTriggeredCreate.current || channelCreateLoading) return;
    if (!participant || !acsToken) return;
    hasTriggeredCreate.current = true;
    const openConversation = async () => {
      const { orgId } = getProgramContext();
      const tag = selectedChatUser.communicationId ? '#admin' : '#self';
      const communicationId = selectedChatUser.communicationId || getUserDetails().communicationUserId;
      const res = await API.getPersonalThreads({ orgId, acsToken, tag, communicationId }).catch(() => undefined);
      const threads = res?.data;
      if (!Array.isArray(threads)) {
        hasTriggeredCreate.current = false;
        return;
      }
      // Several threads can exist with the same person (duplicates made before the BE dedupe): open the
      // one Direct Messages shows, a visible thread with the latest message.
      const withThem = threads.filter((each: any) => (_.get(each, 'displayNames', []) as string[]).includes(commId));
      const existing = _.orderBy(
        withThem,
        [
          (each: any) => !_.has(each, 'deletedOn'),
          (each: any) => Date.parse(_.get(each, 'lastMessageReceivedOn', '')) || 0
        ],
        ['desc', 'desc']
      )[0];
      if (existing?.id) {
        const name = searchParams.get('name') || _.get(existing, 'topic', '');
        navigate(`/app/chat/?threadid=${existing.id}&type=Chats&name=${encodeURIComponent(name)}`, { replace: true });
        return;
      }
      handleClickOnStartChat();
    };
    openConversation();
  }, [commId, participant, acsToken, channelCreateLoading]);

  // const handleCreateGroup = () => {
  //   dispatch(
  //     dialogActions.atnOpenDialog({
  //       dialogName: ACS_CREATE_CHANNEL_DIALOG,
  //       title: 'Create a Channel'
  //     })
  //   );
  // };

  /** Start chat box design */
  const renderChatBox: React.FC<ChatBoxProps> = ({
    icon,
    heading,
    subheading,
    buttonText,
    onClick,
    btnLoading,
    loading,
    disabled
  }) => {
    return (
      <Box gap={'20px'} height="215px" display={'flex'} alignItems={'center'} flexDirection={'column'}>
        {/* Icon */}
        {icon &&
          (loading ? (
            <MySkeleton variant="circular" width={104} height={104} minHeight="104px" />
          ) : (
            <Box
              width={'104px'}
              height={'104px'}
              minHeight={'104px'}
              bgcolor={'#FFFFFF'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius={'50%'}
              boxShadow={'0px 8px 20px rgba(0, 0, 0, 0.16)'}
            >
              <img src={icon} alt="chaticon" height={'66'} width={'66'} />
            </Box>
          ))}

        {/* Heading & Subheading */}
        <Box textAlign="center">
          <Typography variant="h5" color="secondary">
            {loading ? <MySkeleton width={200} /> : heading}
          </Typography>

          {subheading && <Typography mt={1}>{loading ? <MySkeleton width={200} /> : subheading}</Typography>}
        </Box>

        {/* Button - not used anymore; thread is created automatically when commId is present */}
        {buttonText &&
          onClick &&
          (loading ? (
            <MySkeleton variant="rounded" width={162} minHeight={50} />
          ) : (
            <Box />
          ))}
      </Box>
    );
  };

  // Thread is being created for this member: skeleton the whole chat pane (header, messages, composer)
  if (commId) {
    return (
      <Box display="flex" flexDirection="column" height="100%" width="100%">
        <ChatMainHeaderLoader />
        <Box flex={1} minHeight={0} overflow="hidden">
          <ChatMsgContentLoader />
        </Box>
        <ChatEditorLoader />
      </Box>
    );
  }

  return (
    <Box p={3} height={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
      {renderChatBox({
        heading: 'Start a chat',
        subheading: 'Click a person to start a conversation with them',
        loading: loading
      })}
    </Box>
  );
};

export default NoChats;
